import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener('keydown', this.#handleTabKey);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener('keydown', this.#handleTabKey);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  /**
   * Keeps keyboard focus inside the modal drawer in both tab directions.
   *
   * @param {KeyboardEvent} event
   */
  #handleTabKey = (event) => {
    const { dialog } = this.refs;
    if (event.key !== 'Tab' || !dialog.open) return;

    const focusableElements = [...dialog.querySelectorAll(FOCUSABLE_SELECTOR)].filter(
      (element) => element instanceof HTMLElement && element.getClientRects().length > 0
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (!(firstElement instanceof HTMLElement) || !(lastElement instanceof HTMLElement)) return;

    const activeElement = document.activeElement;
    const focusMovedOutside = !dialog.contains(activeElement);

    if (event.shiftKey && (activeElement === firstElement || focusMovedOutside)) {
      event.preventDefault();
      lastElement.focus({ preventScroll: true });
    } else if (!event.shiftKey && (activeElement === lastElement || focusMovedOutside)) {
      event.preventDefault();
      firstElement.focus({ preventScroll: true });
    }
  };

  /**
   * Opens the drawer and restores focus after cart section morphs replace its contents.
   */
  showDialog() {
    super.showDialog();

    requestAnimationFrame(() => {
      if (!this.refs.dialog.open) return;

      const closeButton = this.querySelector('[ref="closeButton"]');
      if (closeButton instanceof HTMLButtonElement) {
        closeButton.focus({ preventScroll: true });
      }
    });
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
